import { toast } from 'react-toastify';
import { useMemo, useState } from 'react';
import {
  useModalData,
  useCloseOldModal,
  useSetUnmountModalCallback,
} from '../../../store/modal/modalHooks';
import './purchase-subscription-modal.scss';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PaymentIframe from '../../shared/PaymentIframe';
import { useCreditInUse } from '../../../store/credits/creditsHooks';
import BookingSelectCreditCard from '../../shared/BookingSelectCreditCard';
import {
  useFetchSubscriptions,
  usePurchaseSubscription,
} from '../../../store/SubscriptionsManagement/subscriptionsManagementHooks';
import { NavigationEnum } from '../../../navigation/vertical/Navigation.enum';
import CancellationPolicy from '../../shared/cancellation-policy/CancellationPolicy';
import BookSuccessMessage from '../../pages/subscription-management/BookSuccessMessage';
import BookModalComponent from '../../shared/book-components/BookModalComponent/BookModalComponent';
import PurchaseSubscriptionForm from '../../pages/subscription-management/PurchaseSubscriptionForm';
import PurchaseSubscriptionInfo from '../../pages/subscription-management/PurchaseSubscriptionInfo';
import { BookFormPhaseEnum } from '../../shared/book-components/BookModalComponent/book-form-phase.enum';
import { SubscriptionManagement } from '../../../store/SubscriptionsManagement/subscriptionsManagementTypes';

const PurchaseSubscriptionModalContainer = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const modalData = useModalData();
  const closeModal = useCloseOldModal();
  const creditInUse = useCreditInUse();
  const fetchSubscriptions = useFetchSubscriptions();
  const purchaseSubscription = usePurchaseSubscription();
  const setUnmountModalCallback = useSetUnmountModalCallback();

  const [orderId, setOrderId] = useState<number>();
  const [showPolicy, setShowPolicy] = useState(false);
  const [iframePaymentUrl, setIframePaymentUrl] = useState<string>();
  const [bookFormPhase, setBookFormPhase] = useState<BookFormPhaseEnum>(
    BookFormPhaseEnum.FORM_PAGE
  );

  const subscription: SubscriptionManagement = modalData?.data.subscription;

  if (!subscription) {
    closeModal();
  }

  const onClickPurchaseSubscription = async () => {
    if (creditInUse.cashAmount) {
      setBookFormPhase(BookFormPhaseEnum.CREDIT_CARD_PAGE);
    } else {
      await onPurchaseSubscription();
    }
  };

  const onPurchaseSubscription = async () => {
    try {
      const response = await purchaseSubscription(
        subscription,
        NavigationEnum.SUBSCRIPTION_MANAGEMENT
      );
      if (response.isPaid) {
        setBookFormPhase(BookFormPhaseEnum.SUCCESS_PAGE);
      }

      if (response.paymentProcessLink) {
        setIframePaymentUrl(response.paymentProcessLink);
        setOrderId(response.orderId);
        setBookFormPhase(BookFormPhaseEnum.PAYMENT_PAGE);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      if (errorMessage) {
        onFailure(errorMessage);
      }
    }
  };

  const onBookPendingPayment = async () => {
    try {
      const response = await purchaseSubscription(
        subscription,
        NavigationEnum.SUBSCRIPTION_MANAGEMENT
      );
      if (response.isPaid) {
        setBookFormPhase(BookFormPhaseEnum.SUCCESS_PAGE);
      }

      if (response.paymentProcessLink) {
        setIframePaymentUrl(response.paymentProcessLink);
        setOrderId(response.orderId);
        setBookFormPhase(BookFormPhaseEnum.PAYMENT_PAGE);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.error?.message;
      if (errorMessage) {
        onFailure(errorMessage);
      }
    }
  };

  const onFailure = (errorMessage: string) => {
    toast.error(errorMessage, {
      icon: false,
      hideProgressBar: true,
      autoClose: 5000,
    });
    closeModal();
    if (history.location.pathname === '/subscriptions') {
      fetchSubscriptions().finally();
    }
  };

  const InfoComponent = useMemo(() => {
    if (showPolicy) {
      return <CancellationPolicy policyId={subscription.policyId || 1} />;
    }
    return <PurchaseSubscriptionInfo subscription={subscription} />;
  }, [showPolicy]);

  const FormComponent = useMemo(() => {
    switch (bookFormPhase) {
      case BookFormPhaseEnum.FORM_PAGE:
        return (
          <PurchaseSubscriptionForm
            subscription={subscription}
            onBookEvent={onClickPurchaseSubscription}
            onSwitchShowPolicy={() => setShowPolicy(!showPolicy)}
            showPolicy={showPolicy}
          />
        );

      case BookFormPhaseEnum.CREDIT_CARD_PAGE:
        return (
          <BookingSelectCreditCard
            onClickSelectCreditCard={onPurchaseSubscription}
            onClickSelectNewCreditCard={onBookPendingPayment}
            onClickBack={() => setBookFormPhase(BookFormPhaseEnum.FORM_PAGE)}
          />
        );

      case BookFormPhaseEnum.PAYMENT_PAGE:
        return (
          <div className="book-event-modal-iframe">
            <PaymentIframe
              url={iframePaymentUrl}
              backText={t('BACK_BUTTON_TEXT')}
              onClickBack={() => {
                setIframePaymentUrl(undefined);
                setBookFormPhase(BookFormPhaseEnum.FORM_PAGE);
              }}
              onPaymentSuccess={() => {
                setBookFormPhase(BookFormPhaseEnum.SUCCESS_PAGE);
                setUnmountModalCallback(undefined);
              }}
            />
          </div>
        );

      case BookFormPhaseEnum.SUCCESS_PAGE:
        return (
          <BookSuccessMessage
            onClick={() => {
              closeModal();
            }}
          />
        );
    }
  }, [bookFormPhase, showPolicy, creditInUse]);

  return (
    <div className="purchase-subscription-modal">
      <BookModalComponent
        infoComponent={InfoComponent}
        formComponent={FormComponent}
      />
    </div>
  );
};

export default PurchaseSubscriptionModalContainer;
